import type { BusinessServiceRequest } from '@ingenico/react-section-utils';
import { usePrevious } from '@react-hookz/web';
import { useMemo } from 'react';

export interface ADeviceRequest<T> {
  readonly data?: T | null;
  readonly hasAllPreviousRequestFailed?: boolean;
  readonly hasAllPreviousRequestSuccedded?: boolean;
  readonly isError: boolean;
  readonly isFirstLoading: boolean;
  readonly isLoading: boolean;
  readonly isRefetching: boolean;
  readonly isWasFetching?: boolean;
  readonly isSuccess: boolean;
}
interface UseProcessMetricsProps<T> {
  readonly requestQuery: BusinessServiceRequest<T>;
  readonly isFirstLoading: boolean;
}
export function useProcessRequest<T>({ requestQuery, isFirstLoading }: UseProcessMetricsProps<T>): ADeviceRequest<T> {
  const { data, isLoadingQuery, isError, isSuccess } = useMemo(
    () => ({
      data: requestQuery.data,
      isLoadingQuery: requestQuery.isLoading,
      isError: requestQuery.isError,
      isSuccess: requestQuery.isSuccess,
    }),
    [requestQuery.data, requestQuery.isError, requestQuery.isLoading, requestQuery.isSuccess],
  );
  const isWasFetching = usePrevious(isLoadingQuery);

  const { isLoading, isRefetching } = useMemo(
    () => ({
      isLoading: requestQuery.isLoading && isFirstLoading,
      isRefetching: requestQuery.isLoading && !isFirstLoading,
    }),
    [requestQuery.isLoading, isFirstLoading],
  );

  const { hasAllPreviousRequestFailed, hasAllPreviousRequestSuccedded } = useMemo(
    () => ({
      hasAllPreviousRequestFailed: isWasFetching && isError,
      hasAllPreviousRequestSuccedded: isWasFetching && isSuccess,
    }),
    [isError, isSuccess, isWasFetching],
  );

  return {
    data,
    hasAllPreviousRequestFailed,
    hasAllPreviousRequestSuccedded,
    isError,
    isFirstLoading,
    isLoading,
    isRefetching,
    isSuccess,
    isWasFetching,
  };
}
